// src/pages/CategoryLocation.js
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL;

const humanize = (str) =>
  str.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

export default function CategoryLocation() {
  const { slug, location } = useParams();

  const categoryName = humanize(slug);
  const locationName = humanize(location);

  const { data, isLoading, error } = useQuery({
    queryKey: ['providers', slug, location],
    queryFn: async () => {
      const res = await axios.get(
        `${API_URL}/api/servicecategory/${slug}/${location}/providers/`
      );
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        Loading providers...
      </div>
    );
  }

  if (error || !data) {
    toast.error('Failed to load providers');
    return (
      <div className="min-h-screen flex items-center justify-center text-red-400">
        No providers found
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      {/* Background blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <Helmet>
        <title>
          {categoryName} in {locationName}, Kenya | Hire Trusted Providers – SErraND
        </title>
        <meta
          name="description"
          content={`Browse verified ${categoryName.toLowerCase()} in ${locationName}, Kenya. Compare providers, pricing, and trust scores on SErraND.`}
        />
        <link
          rel="canonical"
          href={`https://www.serrand.org/categories/${slug}/${location}`}
        />
      </Helmet>

      <section className="relative py-16 px-4 max-w-7xl mx-auto z-10">
        {/* Breadcrumbs */}
        <nav className="text-gray-400 mb-8 text-sm">
          <Link to="/" className="hover:text-white">Home</Link>
          <span className="mx-2">›</span>
          <Link to="/categories" className="hover:text-white">Categories</Link>
          <span className="mx-2">›</span>
          <Link to={`/categories/${slug}`} className="hover:text-white">
            {categoryName}
          </Link>
          <span className="mx-2">›</span>
          <span className="text-white">{locationName}</span>
        </nav>

        {/* H1 */}
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
          {categoryName} in {locationName}
        </h1>

        {/* Intro */}
        <p className="text-xl text-gray-300 max-w-4xl leading-relaxed mb-12">
          Looking for reliable {categoryName.toLowerCase()} in {locationName}?
          SErraND helps you connect with trusted local professionals offering
          verified services, transparent pricing, and real trust scores.
        </p>

        {/* SEO explainer */}
        <section className="max-w-5xl mb-16">
          <h2 className="text-3xl font-bold text-white mb-4">
            Hire Trusted {categoryName} in {locationName}
          </h2>
          <p className="text-gray-300 leading-relaxed mb-4">
            All {categoryName.toLowerCase()} listed on SErraND are grouped by
            location to help you find professionals who actually operate in
            {` ${locationName}`}. Each provider profile includes service details,
            minimum charges, and a trust score based on platform activity.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Whether you need urgent assistance or planned work, browsing local
            providers helps you compare options quickly and hire with confidence.
          </p>
        </section>

        {/* Providers grid */}
        {data.results?.length === 0 ? (
          <p className="text-xl text-gray-300">
            No {categoryName.toLowerCase()} available in this area yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.results.map((prov) => (
              <Link
                key={prov.id}
                to={`/provider/${prov.id}`}
                className="bg-gray-800/60 backdrop-blur-lg border border-gray-700 rounded-2xl overflow-hidden hover:border-purple-500 transition-all hover:shadow-2xl group"
              >
                {prov.images?.[0]?.image && (
                  <img
                    src={prov.images[0].image}
                    alt={`${prov.business_name} in ${locationName}`}
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform"
                  />
                )}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                    {prov.business_name}
                  </h3>
                  <p className="text-gray-300 mb-4 line-clamp-3">
                    {prov.description}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-purple-400 font-medium">
                      Trust Score: {prov.trust_score}
                    </span>
                    <span className="text-gray-400">
                      From KES {prov.min_charge}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="mt-14 flex justify-center gap-10">
          {data.previous && (
            <button className="text-purple-400 hover:text-purple-300">
              ← Previous
            </button>
          )}
          {data.next && (
            <button className="text-purple-400 hover:text-purple-300">
              Next →
            </button>
          )}
        </div>

        {/* Back-links */}
        <div className="mt-20 text-center space-y-3">
          <Link
            to={`/categories/${slug}`}
            className="block text-purple-400 hover:text-purple-300 text-lg font-medium"
          >
            View all {categoryName} locations →
          </Link>
          <Link
            to="/categories"
            className="block text-gray-400 hover:text-gray-300"
          >
            Browse all service categories
          </Link>
        </div>
      </section>
    </div>
  );
}
